import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft,
  Calendar,
  Eye,
  ChevronLeft,
  ChevronRight,
  Share2,
  Link2,
} from "lucide-react";
import MainLayout from "../../components/layout/MainLayout";
import Button from "../../components/common/Button";
import Card from "../../components/common/Card";
import ErrorState from "../../components/common/ErrorState";
import { Skeleton } from "../../components/common/Skeleton";
import { useToast } from "../../context/ToastContext";
import { fetchNotices } from "../../utils/api";
import { formatDate } from "../../utils/formatters";

/**
 * NoticeDetailPage - 공지사항 상세 페이지
 */
const NoticeDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [notices, setNotices] = useState([]);
  const [notice, setNotice] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  const loadNotice = useCallback(async () => {
    try {
      setIsLoading(true);
      setIsError(false);
      const data = await fetchNotices();
      if (!data || data.length === 0) {
        throw new Error("empty");
      }
      // 목록 순서는 최신순(createdAt desc)으로 통일 → 이전/다음 네비게이션 기준
      const sorted = [...data].sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );
      setNotices(sorted);

      const found = sorted.find((n) => n.id === parseInt(id));
      if (found) {
        setNotice(found);
      } else {
        showToast("공지사항을 찾을 수 없습니다.", "error");
        // B5: 잘못된 경로 /support/notice → 실제 목록 라우트 /support/notices
        navigate("/support/notices");
      }
    } catch (error) {
      console.error("Failed to load notice:", error);
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }, [id, navigate, showToast]);

  useEffect(() => {
    loadNotice();
    // 상세 진입 시 본문 상단으로 스크롤
    window.scrollTo({ top: 0 });
  }, [loadNotice]);

  // 현재 글의 목록 내 위치 + 이전/다음 글
  const currentIndex = notice
    ? notices.findIndex((n) => n.id === notice.id)
    : -1;
  const prevNotice = currentIndex > 0 ? notices[currentIndex - 1] : null;
  const nextNotice =
    currentIndex >= 0 && currentIndex < notices.length - 1
      ? notices[currentIndex + 1]
      : null;

  const handleCopyLink = async () => {
    const url = `${window.location.origin}/support/notices/${id}`;
    try {
      await navigator.clipboard.writeText(url);
      showToast("링크가 복사되었습니다.", "success");
    } catch (error) {
      showToast("링크 복사에 실패했습니다.", "error");
    }
  };

  const handleShare = async () => {
    const url = `${window.location.origin}/support/notices/${id}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: notice.title, url });
      } catch (error) {
        // 사용자가 취소한 경우 등은 무시
      }
    } else {
      handleCopyLink();
    }
  };

  if (isLoading) {
    return (
      <MainLayout>
        <div className="max-w-4xl mx-auto px-4 py-8">
          <Skeleton className="mb-6 h-10 w-28" />
          <Card>
            <Skeleton className="mb-3 h-5 w-24" />
            <Skeleton className="mb-4 h-8 w-3/4" />
            <Skeleton className="mb-6 h-4 w-1/2" />
            <Skeleton className="mb-2 h-4 w-full" />
            <Skeleton className="mb-2 h-4 w-full" />
            <Skeleton className="h-4 w-2/3" />
          </Card>
        </div>
      </MainLayout>
    );
  }

  if (isError) {
    return (
      <MainLayout>
        <div className="max-w-4xl mx-auto px-4 py-8">
          <ErrorState
            title="공지사항을 불러오지 못했습니다"
            message="네트워크 상태를 확인한 후 다시 시도해주세요."
            onRetry={loadNotice}
          />
        </div>
      </MainLayout>
    );
  }

  if (!notice) {
    return null;
  }

  const isUpdated =
    notice.updatedAt && notice.updatedAt !== notice.createdAt;

  return (
    <MainLayout>
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* 뒤로 가기 + 위치 표시 */}
        <div className="mb-6 flex items-center justify-between">
          <Button
            variant="outline"
            onClick={() => navigate("/support/notices")}
          >
            <ArrowLeft size={16} className="mr-2" />
            목록으로
          </Button>
          {currentIndex >= 0 && (
            <span className="text-sm text-accent-500">
              {currentIndex + 1} / {notices.length}
            </span>
          )}
        </div>

        {/* 공지사항 상세 */}
        <Card>
          {/* 헤더 */}
          <div className="border-b border-accent-200 pb-4 mb-6">
            <div className="flex items-center gap-2 mb-3">
              {notice.isImportant && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800">
                  중요
                </span>
              )}
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-accent-100 text-accent-700">
                {notice.category}
              </span>
            </div>
            <h1 className="text-2xl font-bold text-primary-900 mb-4">
              {notice.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-accent-600">
              <div className="flex items-center gap-1">
                <Calendar size={16} />
                <span>{formatDate(notice.createdAt)}</span>
              </div>
              {isUpdated && (
                <span className="text-xs text-accent-500">
                  (수정: {formatDate(notice.updatedAt)})
                </span>
              )}
              <div className="flex items-center gap-1">
                <Eye size={16} />
                <span>조회 {notice.views.toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* 내용 */}
          <div className="prose max-w-none">
            <div className="text-primary leading-relaxed whitespace-pre-line">
              {notice.content}
            </div>
          </div>

          {/* 공유 / 복사 */}
          <div className="mt-6 flex flex-wrap gap-2 border-t border-accent-200 pt-4">
            <Button variant="outline" size="sm" onClick={handleShare}>
              <Share2 size={14} className="mr-1.5" />
              공유하기
            </Button>
            <Button variant="outline" size="sm" onClick={handleCopyLink}>
              <Link2 size={14} className="mr-1.5" />
              링크 복사
            </Button>
          </div>
        </Card>

        {/* 이전 / 다음 글 네비게이션 */}
        <div className="mt-6 divide-y divide-accent-100 rounded-lg border border-accent-200 bg-white">
          <div className="flex items-center gap-3 px-4 py-3">
            <span className="flex w-12 flex-shrink-0 items-center gap-1 text-xs font-medium text-accent-500">
              <ChevronLeft size={14} />
              이전
            </span>
            {prevNotice ? (
              <Link
                to={`/support/notices/${prevNotice.id}`}
                className="line-clamp-1 text-sm text-primary hover:text-primary-700 hover:underline"
              >
                {prevNotice.title}
              </Link>
            ) : (
              <span className="text-sm text-accent-400">이전 글이 없습니다</span>
            )}
          </div>
          <div className="flex items-center gap-3 px-4 py-3">
            <span className="flex w-12 flex-shrink-0 items-center gap-1 text-xs font-medium text-accent-500">
              <ChevronRight size={14} />
              다음
            </span>
            {nextNotice ? (
              <Link
                to={`/support/notices/${nextNotice.id}`}
                className="line-clamp-1 text-sm text-primary hover:text-primary-700 hover:underline"
              >
                {nextNotice.title}
              </Link>
            ) : (
              <span className="text-sm text-accent-400">다음 글이 없습니다</span>
            )}
          </div>
        </div>

        {/* 하단 버튼 */}
        <div className="mt-6 flex justify-center">
          <Button
            variant="primary"
            onClick={() => navigate("/support/notices")}
          >
            목록으로 돌아가기
          </Button>
        </div>
      </div>
    </MainLayout>
  );
};

export default NoticeDetailPage;
